import { apiClient } from "./client";
import type {
  OpenShift,
  OpenShiftApplication,
  OpenShiftStatus,
} from "./types";

export type ListOpenShiftParams = {
  organization_id?: string;
  status?: OpenShiftStatus;
};

export async function listOpenShifts(params: ListOpenShiftParams = {}): Promise<OpenShift[]> {
  const { data } = await apiClient.get<OpenShift[]>("/api/v1/open-shifts", { params });
  return data;
}

export type CreateOpenShiftPayload = {
  organization_id: string;
  date: string;
  slot_template_id: string;
  exercise_mode_id: string;
  title?: string | null;
  description?: string | null;
  total_places: number;
  closes_at?: string | null;
  reward_bonus_points?: number;
};

export async function createOpenShift(p: CreateOpenShiftPayload): Promise<OpenShift> {
  const { data } = await apiClient.post<OpenShift>("/api/v1/open-shifts", p);
  return data;
}

export async function updateOpenShift(
  id: string,
  p: Partial<{
    title: string;
    description: string;
    total_places: number;
    reward_bonus_points: number;
    status: OpenShiftStatus;
  }>,
): Promise<OpenShift> {
  const { data } = await apiClient.patch<OpenShift>(`/api/v1/open-shifts/${id}`, p);
  return data;
}

export async function deleteOpenShift(id: string): Promise<void> {
  await apiClient.delete(`/api/v1/open-shifts/${id}`);
}

export async function publishOpenShift(id: string): Promise<OpenShift> {
  const { data } = await apiClient.post<OpenShift>(`/api/v1/open-shifts/${id}/publish`);
  return data;
}

export async function cancelOpenShift(id: string): Promise<OpenShift> {
  const { data } = await apiClient.post<OpenShift>(`/api/v1/open-shifts/${id}/cancel`);
  return data;
}

// ----------------------------------------------------------------------------
// Applications
// ----------------------------------------------------------------------------
export async function listApplications(shiftId: string): Promise<OpenShiftApplication[]> {
  const { data } = await apiClient.get<OpenShiftApplication[]>(
    `/api/v1/open-shifts/${shiftId}/applications`,
  );
  return data;
}

export async function applyToOpenShift(
  shiftId: string,
  cover_text?: string,
): Promise<OpenShiftApplication> {
  const { data } = await apiClient.post<OpenShiftApplication>(
    `/api/v1/open-shifts/${shiftId}/applications`,
    { cover_text },
  );
  return data;
}

export async function withdrawApplication(
  shiftId: string,
  applicationId: string,
): Promise<void> {
  await apiClient.delete(`/api/v1/open-shifts/${shiftId}/applications/${applicationId}`);
}

export async function listMyApplications(): Promise<OpenShiftApplication[]> {
  const { data } = await apiClient.get<OpenShiftApplication[]>(
    "/api/v1/me/open-shift-applications",
  );
  return data;
}

export async function acceptApplication(
  shiftId: string,
  applicationId: string,
  reason?: string,
): Promise<OpenShiftApplication> {
  const { data } = await apiClient.post<OpenShiftApplication>(
    `/api/v1/open-shifts/${shiftId}/applications/${applicationId}/accept`,
    { reason },
  );
  return data;
}

export async function rejectApplication(
  shiftId: string,
  applicationId: string,
  reason?: string,
): Promise<OpenShiftApplication> {
  const { data } = await apiClient.post<OpenShiftApplication>(
    `/api/v1/open-shifts/${shiftId}/applications/${applicationId}/reject`,
    { reason },
  );
  return data;
}


export type EligiblePractitioner = {
  id: string;
  display_name: string;
  first_name: string | null;
  last_name: string;
  phone: string | null;
  email: string | null;
  skill_ids: string[];
};

export type EligibleList = {
  required_skill_ids: string[];
  items: EligiblePractitioner[];
};

export async function listEligiblePractitioners(
  shiftId: string,
): Promise<EligibleList> {
  const { data } = await apiClient.get<EligibleList>(
    `/api/v1/open-shifts/${shiftId}/eligible-practitioners`,
  );
  return data;
}
